/* HTTP client for the PalinDragon API.
   Implements the contract in briefs/api.md:

   - GET /today    → { puzzle, game_id }
   - POST /complete (Playthrough) → 204 (always; the server never tells the
                                          client which check it failed).

   Both endpoints live behind https://api.palindragon.com — the public
   CloudFront-fronted host from Phase 1.4. The private origin
   (api-origin.<domain>) is reachable only with the x-origin-secret header
   the CloudFront origin layer injects; the front-end never needs to know
   about it.

   Single-flight: in-flight `getToday()` calls share one promise so a
   double-mount in StrictMode (or tests that race the loader) doesn't
   mint two game tokens.

   Fire-and-forget: postComplete catches every throw and treats every
   non-2xx as silent. The contract in api.md is "204 on every failure";
   the front-end honors that by never surfacing one. */

import type { Puzzle } from "./puzzle";
import type { Playthrough } from "./playthrough";

// In dev (Vite), API calls are routed through the Vite dev server's
// proxy — see web/vite.config.ts. This makes the browser's request
// same-origin (localhost:5173) so production CORS doesn't apply, and
// CloudFront's edge layer adds the x-origin-secret header server-side.
// In production builds the API host is reached directly.
export const API_BASE = import.meta.env.DEV
  ? "/api-proxy"
  : "https://api.palindragon.com";

/** Wire shape for the `puzzle` field of GET /today, mirroring
   lambda/shared::PuzzlePublic. snake_case here matches the JSON. */
export interface PuzzleWire {
  number: number;
  date: string;
  mask: string;
  letters: number;
  max_guesses: number;
  hint: string;
  byline: string;
  solution_normalized: string;
  text: string;
}

/** Wire shape for GET /today (the full envelope). */
export interface TodayWire {
  puzzle: PuzzleWire;
  game_id: string;
}

/** Adapter result: front-end-shaped Puzzle + the server-issued game id. */
export interface TodayResponse {
  puzzle: Puzzle;
  gameId: string;
}

/** Translate the wire shape into the front-end's camelCase Puzzle.
   `text` and `byline` are carried through verbatim — the writer
   (bin/sync-puzzles → typography pass) has already converted ASCII
   straight punctuation to typographic forms, so the boundary just
   trusts what the API returned. */
export function mapWireToPuzzle(wire: PuzzleWire): Puzzle {
  // Boundary check: a stale Lambda (front-end deployed ahead of API)
  // returns the older wire shape without `text`. analyzePuzzle would
  // then crash on undefined; throw with a clear message so the
  // load-error surface in BoardScroll renders something useful.
  if (typeof wire.text !== "string" || wire.text.length === 0) {
    throw new ApiError(500, "API response missing 'text' field — Lambda may need redeploy");
  }
  return {
    number: wire.number,
    date: wire.date,
    text: wire.text,
    sentence: wire.text,
    mask: wire.mask,
    letters: wire.letters,
    solutionNormalized: wire.solution_normalized,
    byline: wire.byline,
    hint: wire.hint,
    maxGuesses: wire.max_guesses,
  };
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

let inFlight: Promise<TodayResponse> | null = null;

/** Fetch today's puzzle + a fresh game id. Single-flight: parallel
   callers share the returned promise so we don't double-mint tokens. */
export async function getToday(): Promise<TodayResponse> {
  if (inFlight != null) return inFlight;
  inFlight = (async (): Promise<TodayResponse> => {
    try {
      const res = await fetch(`${API_BASE}/today`);
      if (!res.ok) {
        // Read the body for log context but don't include sensitive
        // server detail in the throw — the message is the surface
        // for the test suite and operator console; a 404 means the
        // publishing pipeline missed today's drop.
        let body = "";
        try {
          body = await res.text();
        } catch {
          /* ignore */
        }
        throw new ApiError(res.status, `GET /today ${res.status}: ${body}`);
      }
      const wire = (await res.json()) as TodayWire;
      return {
        puzzle: mapWireToPuzzle(wire.puzzle),
        gameId: wire.game_id,
      };
    } finally {
      // Clear after settle (both success and failure) so the next
      // page-level reload starts fresh — but only after the awaiting
      // callers have observed the value.
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Test-only: clear the single-flight cache. Production code never
   needs this — every page load mints exactly one game id by virtue
   of the module being instantiated once per document. */
export function __resetInFlight(): void {
  inFlight = null;
}

/** POST a finished playthrough to /complete. Fire-and-forget.
   Resolves on success, on transport failure, on 4xx/5xx — anything.
   Never throws. The server's 204-on-everything contract means a
   meaningful return value is impossible by design; the local
   localStorage record is the durable copy. */
export async function postComplete(playthrough: Playthrough): Promise<void> {
  try {
    await fetch(`${API_BASE}/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(playthrough),
      // keepalive lets the browser dispatch this even if the page is
      // unloading (e.g., the player closes the tab on terminal state).
      keepalive: true,
    });
  } catch {
    /* swallow — analytics-class side-effect; never affects gameplay */
  }
}
